import type React from 'react';
import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import RemoveIcon from 'assets/icons/icn _ 16px _ remove_dark.svg?react';
import GroupAddIcon from 'assets/icons/icn _ 20px _ group-add.svg?react';
import SwapPlayerIcon from 'assets/icons/icn _ 24px _ alternate.svg?react';
import UnpairIcon from 'assets/icons/icn _ 24px _ swap-horizontal.svg?react';
import MoveIcon from 'assets/icons/icn _ 24px _ swap-vertical.svg?react';
import AddIcon from 'assets/icons/icn-glyph-24-add.svg?react';
import WithdrawIcon from 'assets/icons/icn-glyph-24-remove.svg?react';
import { ActionsMenu, ActionsMenuItem } from 'components/popovers/actions/ActionsMenu';

export interface CompetitorsAvailableActions {
  ADD_TO_DRAW?: boolean;
  MOVE_TO_ALTERNATES?: boolean;
  MOVE_TO_AVAILABLE_PLAYERS?: boolean;
  MOVE_TO_QUALIFYING?: boolean;
  MOVE_TO_SELECTED?: boolean;
  MOVE_TO_TEAM?: boolean;
  MOVE_TO_UNPAIRED?: boolean;
  PROMOTE_TO_QUALIFYING?: boolean;
  PROMOTE_TO_SELECTED?: boolean;
  REMOVE_FROM_DRAW?: boolean;
  REMOVE_FROM_FLIGHT?: boolean;
  REPLACE_DOUBLES_PLAYER?: boolean;
  UNPAIR_PLAYERS?: boolean;
  WITHDRAW_TO_ALTERNATES?: boolean;
  WITHDRAW?: boolean;
}

interface CompetitorsActionsProps {
  actions: CompetitorsAvailableActions;
  open: boolean;
  anchorEl: () => HTMLElement | null;
  onAddToDraw: () => void;
  onClose: () => void;
  onMoveToAlternates: () => void;
  onMoveToQualifying: () => void;
  onMoveToSelected: () => void;
  onMoveToTeam: () => void;
  onPromoteToMain: () => void;
  onPromoteToQualifying: () => void;
  onRemoveFromDraw: () => void;
  onRemoveFromFlight: () => void;
  onReplaceDoublesPlayer: () => void;
  onUnpairPlayers: () => void;
  onWithdrawal: () => void;
  onWithdrawToAlternates: () => void;
}

const CompetitorsActions: React.FC<React.PropsWithChildren<CompetitorsActionsProps>> = ({
  actions,
  open,
  anchorEl,
  onAddToDraw,
  onClose,
  onMoveToAlternates,
  onMoveToQualifying,
  onMoveToSelected,
  onMoveToTeam,
  onPromoteToMain,
  onPromoteToQualifying,
  onRemoveFromDraw,
  onRemoveFromFlight,
  onReplaceDoublesPlayer,
  onUnpairPlayers,
  onWithdrawal,
  onWithdrawToAlternates,
}) => {
  const { t } = useTranslation();

  return useMemo(
    () => (
      <ActionsMenu anchorEl={anchorEl} id="team-actions-popover" open={open} onClose={onClose}>
        {actions.ADD_TO_DRAW && <ActionsMenuItem icon={<AddIcon />} text={t('addToDraw')} onClick={onAddToDraw} />}
        {actions.REMOVE_FROM_DRAW && (
          <ActionsMenuItem icon={<RemoveIcon />} text={t('removeFromDraw')} onClick={onRemoveFromDraw} />
        )}
        {actions.PROMOTE_TO_SELECTED && (
          <ActionsMenuItem icon={<AddIcon />} text={t('moveToSelected')} onClick={onPromoteToMain} />
        )}
        {actions.MOVE_TO_SELECTED && (
          <ActionsMenuItem icon={<AddIcon />} text={t('moveToSelected')} onClick={onMoveToSelected} />
        )}
        {actions.MOVE_TO_QUALIFYING && (
          <ActionsMenuItem icon={<MoveIcon />} text={t('moveToQualifiers')} onClick={onMoveToQualifying} />
        )}
        {actions.PROMOTE_TO_QUALIFYING && (
          <ActionsMenuItem icon={<MoveIcon />} text={t('moveToQualifiers')} onClick={onPromoteToQualifying} />
        )}
        {actions.MOVE_TO_TEAM && <ActionsMenuItem icon={<AddIcon />} text={t('moveToTeam')} onClick={onMoveToTeam} />}
        {actions.MOVE_TO_AVAILABLE_PLAYERS && (
          <ActionsMenuItem icon={<GroupAddIcon />} text={t('moveToAvailablePlayers')} onClick={onPromoteToMain} />
        )}
        {actions.MOVE_TO_UNPAIRED && (
          <ActionsMenuItem icon={<MoveIcon />} text={t('moveToUnpairedPlayers')} onClick={onMoveToAlternates} />
        )}
        {actions.MOVE_TO_ALTERNATES && (
          <ActionsMenuItem icon={<MoveIcon />} text={t('moveToAlternates')} onClick={onMoveToAlternates} />
        )}
        {actions.WITHDRAW_TO_ALTERNATES && (
          <ActionsMenuItem icon={<MoveIcon />} text={t('moveToAlternates')} onClick={onWithdrawToAlternates} />
        )}
        {actions.UNPAIR_PLAYERS && (
          <ActionsMenuItem icon={<UnpairIcon />} text={t('unpairPlayers')} onClick={onUnpairPlayers} />
        )}
        {actions.REPLACE_DOUBLES_PLAYER && (
          <ActionsMenuItem icon={<SwapPlayerIcon />} text={t('replacePlayer')} onClick={onReplaceDoublesPlayer} />
        )}
        {actions.REMOVE_FROM_FLIGHT && (
          <ActionsMenuItem icon={<RemoveIcon />} text={t('removeFromFlight')} onClick={onRemoveFromFlight} />
        )}
        {actions.WITHDRAW && <ActionsMenuItem icon={<WithdrawIcon />} text={t('withdraw')} onClick={onWithdrawal} />}
      </ActionsMenu>
    ),
    [
      open,
      anchorEl,
      onClose,
      actions.ADD_TO_DRAW,
      actions.REMOVE_FROM_DRAW,
      actions.PROMOTE_TO_SELECTED,
      actions.MOVE_TO_SELECTED,
      actions.MOVE_TO_QUALIFYING,
      actions.PROMOTE_TO_QUALIFYING,
      actions.MOVE_TO_TEAM,
      actions.MOVE_TO_AVAILABLE_PLAYERS,
      actions.MOVE_TO_UNPAIRED,
      actions.MOVE_TO_ALTERNATES,
      actions.WITHDRAW_TO_ALTERNATES,
      actions.UNPAIR_PLAYERS,
      actions.REPLACE_DOUBLES_PLAYER,
      actions.REMOVE_FROM_FLIGHT,
      actions.WITHDRAW,
      t,
      onAddToDraw,
      onRemoveFromDraw,
      onPromoteToMain,
      onMoveToSelected,
      onMoveToQualifying,
      onPromoteToQualifying,
      onMoveToTeam,
      onMoveToAlternates,
      onWithdrawToAlternates,
      onUnpairPlayers,
      onReplaceDoublesPlayer,
      onRemoveFromFlight,
      onWithdrawal,
    ],
  );
};

export default CompetitorsActions;
